import { useEffect } from "react";
import { QueryClient } from "@tanstack/react-query";
import simpleRestProvider from "ra-data-simple-rest";
import {
  Admin,
  CustomRoutes,
  fetchUtils,
  Loading,
  Resource,
  useStore,
} from "react-admin";
import achpayments from "./achpayments/v2/";
import AdminsIndex from "./admins/AdminsIndex";
import applications from "./applications/";
import BusinessCalculatorShow from "./applications/detailpages/BusinessCalculatorShow";
import FinancialConditionShow from "./applications/detailpages/FinancialConditionShow";
import TransUnionShow from "./applications/detailpages/TransUnionShow";
import authProvider from "./authProvider";
import bank_onespan_accounts from "./bank_onespan_accounts";
import bankers from "./bankers";
import banks from "./banks";
import counselors from "./counselors";
import customers from "./customers";
import { Dashboard } from "./dashboard";
import employers from "./employers";
import EmployerVerifyPage from "./employers/EmployerVerifyPage";
import VendorVerifyPage from "./employers/VendorVerifyPage";
import { Layout, Login } from "./layout";
import ForgetPasswordPage from "./layout/ForgetPasswordPage";
import ResetPasswordPage from "./layout/ResetPasswordPage";
import loans from "./loans";
import NotFoundPage from "./NotFoundPage";
import products from "./products";
import ProductEdit from "./products/ProductEdit";
import rans from "./rans";
import BankReport from "./reports/BankReport";
import requestdata from "./requestdata";
import TabPages from "./TabPages";
import { ThemeName, themes } from "./themes/themes";
import Profile from "./unit/Profile";
import RanDemo from "./unit/Randemo";
import RanTest from "./unit/RanTest";
import reports from "./reports";
import vendors from "./vendors";
import campaigns from "./campaigns";
import couponcodes from "./couponcodes";
import PaymentSchedulePage from "./paymentSchedule/PaymentSchedulePage";
import DownloadInvoiceByPdfLink from "./unit/DownloadInvoiceByPdfLink";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import VerifyBankerLogin from "./layout/VerifyBankerLogin";
import campaigns_applications from "./campaigns_applications";
import couponcodes_applications from "./couponcodes_applications";
import DailyReportList from "./reports_daily/DailyReportList";
import DailyReportShow from "./reports_daily/DailyReportShow";
import polyglotI18nProvider from "ra-i18n-polyglot";
import en from "./i18n/en";
import HmLoading from "./unit/HmLoading";
import Register from "./layout/Register";
import RegisterVerify from "./layout/RegisterVerify";
// import Refill from "./achpayments/v2/Refill";
import DownloadOtherPdf from "./unit/DownloadOtherPdf";
import RefillsPaymentsList from "./achpayments/v2/RefillsPaymentsList";
import {
  clearStoredAuthState,
  ensureFreshToken,
  getSecondsUntilTokenExpiry,
  handleExpiredSession,
  isStandaloneRoute,
  isPublicRoute,
  refreshToken,
} from "./authProvider";
import counselors_applications from "./counselors_applications";
import employers_applications from "./employers_applications";
import vendors_applications from "./vendors_applications";
import { ensureZendeskWidget } from "./utils/zendeskWidget";
export const i18nProvider = polyglotI18nProvider(() => en, "en");
const APP_VERSION = import.meta.env.VITE_APP_VERSION;
// console.log("App Version: ", APP_VERSION);
const fetchJson = async (url: string, options: fetchUtils.Options = {}) => {
  await ensureFreshToken();

  const requestHeaders = new Headers(
    options.headers ?? { Accept: "application/json" }
  );
  const requestOptions = {
    ...options,
    headers: requestHeaders,
  };

  requestHeaders.set("reqDivision", localStorage.getItem("division") || "");
  requestHeaders.set("reqBank", localStorage.getItem("bank") || "");
  requestHeaders.set("reqAdmin", localStorage.getItem("permissions") || "");
  requestHeaders.set("reqType", ""); // '' or accont

  const access_token = localStorage.getItem("access_token");
  requestHeaders.set("Authorization", `Bearer ${access_token}`);

  // const csrfToken = localStorage.getItem("XSRF-TOKEN");
  // options.headers.set("X-XSRF-TOKEN", csrfToken);
  requestHeaders.set("X-XSRF-TOKEN", "");

  try {
    return await fetchUtils.fetchJson(url, requestOptions);
  } catch (error) {
    const requestError = error as { status?: number };

    if (requestError.status === 401 && getSecondsUntilTokenExpiry() <= 0) {
      try {
        await refreshToken();
        requestHeaders.set(
          "Authorization",
          `Bearer ${localStorage.getItem("access_token")}`
        );
        return await fetchUtils.fetchJson(url, requestOptions);
      } catch (refreshError) {
        handleExpiredSession();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
};

const apibaseurl = import.meta.env.VITE_API_URL;

const dataProviderFactory = simpleRestProvider(apibaseurl, fetchJson);

// main application component
function MainApp() {
  const [themeName] = useStore<ThemeName>("themeName", "hm");
  const lightTheme = themes.find((theme) => theme.name === themeName)?.light;
  const location = useLocation();
  const accessToken = localStorage.getItem("access_token");

  const isSpecialRoute = isStandaloneRoute(location.pathname);

  useEffect(() => {
    if (!accessToken || location.pathname === "/login") {
      return;
    }

    void ensureZendeskWidget();
  }, [accessToken, location.pathname]);

  //if special route, return null and let outer Routes handle it
  if (isSpecialRoute) {
    return null;
  }

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: false,
        structuralSharing: false,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retryDelay: 10000,
      },
    },
  });
  const CustomLoading = () => {
    return <HmLoading />;
  };

  return (
    <Admin
      title=""
      dataProvider={dataProviderFactory}
      authProvider={authProvider}
      dashboard={Dashboard}
      loginPage={Login}
      layout={Layout}
      disableTelemetry
      lightTheme={lightTheme}
      defaultTheme="light"
      theme={lightTheme}
      queryClient={queryClient}
      catchAll={NotFoundPage}
      // basename=""
      // i18nProvider={i18nProvider}
      loading={CustomLoading}
      //custom loading text
    >
      <Resource name="rans" {...rans} />
      <Resource name="banks" {...banks} />
      <Resource name="bankers" {...bankers} />
      <Resource name="applications" {...applications} />
      <Resource name="loans" {...loans} />
      <Resource name="customers" {...customers} />
      <Resource name="bank_onespan_accounts" {...bank_onespan_accounts} />
      <Resource name="achpayment" {...achpayments} />
      <Resource name="repayment" {...achpayments} />
      <Resource name="disbursement" {...achpayments} />
      <Resource name="refund" {...achpayments} />
      {/* <Resource name="refill" {...achpayments} /> */}
      <Resource name="refill" list={RefillsPaymentsList} />
      <Resource name="randemodata" />
      <Resource name="employers" {...employers} />
      <Resource name="counselors" {...counselors} />
      <Resource
        name="counselorsToApplications"
        list={counselors_applications.applist}
      />
      <Resource
        name="counselorsToLoans"
        list={counselors_applications.loanlist}
      />
      <Resource
        name="employersToApplications"
        list={employers_applications.applist}
      />
      <Resource
        name="employersToLoans"
        list={employers_applications.loanlist}
      />
      <Resource
        name="vendorsToApplications"
        list={vendors_applications.applist}
      />
      <Resource name="vendorsToLoans" list={vendors_applications.loanlist} />
      <Resource name="vendors" {...vendors} />
      <Resource name="reports" {...reports} />
      <Resource name="monthreport" list={DailyReportList} />
      <Resource name="campaigns" {...campaigns} />
      <Resource name="couponcodes" {...couponcodes} />
      <Resource name="products" {...products} />
      <Resource name="requestdata" {...requestdata} />
      <Resource name="campaignsToApplications" {...campaigns_applications} />
      <Resource
        name="couponcodesToApplications"
        {...couponcodes_applications}
      />

      <CustomRoutes>
        <Route
          path="/applications/:appId/credit_report/show"
          element={<TransUnionShow />}
        />
        <Route
          path="/applications/:appId/financial_transaction/show"
          element={<FinancialConditionShow />}
        />
        <Route
          path="/applications/:appId/debt_income_ratio/show"
          element={<BusinessCalculatorShow />}
        />
        <Route
          path="/monthreportDetail/:reqDate/show"
          element={<DailyReportShow />}
        />
        <Route path="/admins" element={<AdminsIndex />} />
        {/* <Route path="/reports" element={<BankReport />} /> */}
        {/* <Route path="/randemo" element={<RanDemo />} /> */}
        {/* <Route path="/test" element={<RanTest />} /> */}
        <Route path="/profile" element={<Profile />} />
        {/* <Route path="/refillnew" element={<Refill />} /> */}
        <Route path="/product/edit/:pid" element={<ProductEdit />} />
        <Route path="/tabpages/*" element={<TabPages />} />
      </CustomRoutes>
    </Admin>
  );
}

export const App = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/paymentSchedule/:token"
          element={<PaymentSchedulePage />}
        />
        <Route path="/pdfs/:hash" element={<DownloadInvoiceByPdfLink />} />
        {/* <Route
          path="/employer-verify/:token"
          element={<EmployerVerifyPage />}
        /> */}
        <Route path="/employerVerify/:token" element={<EmployerVerifyPage />} />
        {/* <Route path="/vendor-verify/:token" element={<VendorVerifyPage />} /> */}
        <Route path="/vendors/verify/:token" element={<VendorVerifyPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/forget-password" element={<ForgetPasswordPage />} />
        <Route path="/verify-banker" element={<VerifyBankerLogin />} />
        <Route path="/register/banker" element={<Register />} />
        <Route path="/verifyBanker/:token" element={<RegisterVerify />} />
        <Route
          path="/loanmodifypdf/:token"
          element={<DownloadOtherPdf type="loan_modify" />}
        />

        <Route path="/*" element={<MainApp />} />
      </Routes>
    </BrowserRouter>
  );
};
